import { useMemo, useState } from 'react'
import { PixelPet } from '../identity/PixelPet'
import { ProjectAvatar } from '../identity/ProjectAvatar'
import {
  ACTIVITY_MAX,
  ACTIVITY_TABS,
  filterActivity,
  relativeTime,
  type ActivityItem,
  type ActivityTab,
} from './feed'
import { useActivity } from './useActivity'
import { QueueList } from '../agents/QueueList'

/**
 * Activity — every agent's messages on one timeline, newest first.
 *
 * Layout is Slack's Activity tab, per the owner's screenshots: a filter row of
 * pills, then rows of [avatar with a source badge] · [name + right-aligned relative
 * time] · [where it happened] · [two lines of preview]. The parts that are NOT
 * copied are the ones our data cannot honestly fill — Slack's Threads and Mentions
 * filters have no equivalent here, so the pills are All / Agents / Projects /
 * Unread instead of pretending.
 *
 * The footer note is not decoration. This feed is complete for project rooms and
 * deliberately partial for the agent bus, and a feed that looks complete while
 * silently omitting half of what an agent did is exactly the failure that never
 * gets reported — it reads as "the agents were quiet".
 *
 * A BODY OF THE RAIL, NOT A SHEET OVER IT — owner, 2026-09-20: 「把下方 Menu 的
 * Activity 欄位也使用整個把面板換成為它裡面所有內容的方法,現在這種跳出來蓋板的顯示
 * 也是非常的不理想。就如同我們之前置換 More 這個按鈕一樣的行為」.
 *
 * It renders in the same flex slot as #sbList, #serverMenu and #sbMore, so the
 * nav bar below it never moves and the stage behind the rail is untouched. The
 * sheet version had to be shortened on a phone so it would not bury the very nav
 * that opened it — a whole block of arithmetic in activity.css computing how much
 * of the pill to clear. A body cannot cover the nav at all, so that block is gone
 * rather than corrected: the structure now makes the bug unrepresentable, which is
 * the owner's reason for asking.
 *
 * ⛔ NO VAULT NOTICE. It said "The vault is locked, so agent rows appear without
 * their text until you unlock", and the owner met it on a brand-new empty
 * account (2026-09-21): 「這個文字是不對的,需要整個把它拿掉,因為我們目前的機制沒有
 * 任何的 Vault」.
 *
 * It was not an edge case — it showed for EVERYONE. This deployment never
 * configures a vault (/security/status answers configured:false), so `unlocked`
 * is false for every account that has ever signed in, and the line was printed
 * unconditionally under the filters. A first-run screen opening with an
 * instruction to unlock something that cannot exist is the worst possible first
 * sentence.
 *
 * The identical bug was reported and fixed for the terminal status line on
 * 2026-08-08 — 「還是時常跑出來 vault locked 的信息 … 需要修掉這個 legacy bug」 —
 * and the rule from it (App.tsx: name the vault only where one exists) simply
 * never reached this panel. One door along, again.
 *
 * NO onClose, and that is what being a destination means. A row opens what it is
 * about and the rail STAYS on Activity — on a phone `pushDetail` has already slid
 * the rail away to show the conversation, and coming back to the tab you were on
 * is what every app with a bottom bar does. Agents is the way out.
 */
export function ActivityPanel({
  selfEmail,
  onOpenProject,
  onOpenDm,
  onOpenAgent,
}: {
  selfEmail: string | null
  onOpenProject: (projectId: string) => void
  /** A friend DM row: opened by the peer's email, the way the rail opens one. */
  onOpenDm: (peer: string) => void
  onOpenAgent: (session: string, host: string | null) => void
}) {
  const [tab, setTab] = useState<ActivityTab>('all')
  const { items, loading, truncated, failed, marked } = useActivity(true, selfEmail)

  /**
   * One clock for the whole render, in SECONDS to match the wire.
   *
   * Read once rather than per row: two rows either side of a minute boundary would
   * otherwise round against each other, and the list would show "2m ago" above
   * "1m ago" for messages that arrived in that order.
   */
  const now = Date.now() / 1000

  const rows = useMemo(() => filterActivity(items, tab), [items, tab])

  return (
    <nav id="sbActivity" aria-label="Activity">
      <div className="act-scroll">
      <div className="act-tabs" role="tablist" aria-label="Filter activity">
        {ACTIVITY_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`act-tab${tab === t.id ? ' on' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* A different list, not a filter — see ActivityTab. Rendered before the feed's
          own states so a locked vault does not explain away an empty queue: /agent/*
          sits outside that gate, so the queue is readable either way and saying
          otherwise would be a confident answer about the wrong thing. */}
      {tab === 'queue' ? (
        <QueueList />
      ) : (
      <>
      {loading && rows.length === 0 && <div className="act-empty">Loading…</div>}

      {!loading && rows.length === 0 && (
        <div className="act-empty">
          {tab === 'unread' ? 'Nothing unread.' : 'Nothing has happened yet.'}
        </div>
      )}

      <div className="act-list">
        {rows.map((item) => (
          <ActivityRow
            key={item.key}
            item={item}
            now={now}
            onOpen={() => {
              if (item.open.kind === 'project') onOpenProject(item.open.id)
              else if (item.open.kind === 'dm') onOpenDm(item.open.peer)
              else onOpenAgent(item.open.session, item.open.host)
            }}
          />
        ))}
      </div>

      {failed.length > 0 && (
        <div className="act-note act-err">
          Couldn’t load: {failed.join(', ')} — those rooms are missing from this list,
          not empty.
        </div>
      )}

      {truncated && (
        <div className="act-note">
          Showing the newest {ACTIVITY_MAX} events — there are older ones.
        </div>
      )}

      {/*
        What this list can and cannot see. Kept, and rewritten, because the limits
        it described have changed: the sender, the bus log and last_said_ts all
        landed after the first version shipped. A stale caveat is worse than none —
        it teaches a limit that is no longer there.
      */}
      <div className="act-note">
        Project posts are complete. Agent-to-agent messages go back 200 events and
        are held in memory, so a server restart empties them; older ones survive
        only as each agent’s newest. “Said in its terminal” is the latest line per
        agent — the full run is in that agent’s own view.
        {marked > 0 && ` · ${marked} marked unread here.`}
      </div>
      </>
      )}
      </div>
    </nav>
  )
}

function ActivityRow({
  item,
  now,
  onOpen,
}: {
  item: ActivityItem
  now: number
  onOpen: () => void
}) {
  return (
    <button type="button" className={`act-row${item.unread ? ' unread' : ''}`} onClick={onOpen}>
      {/*
        44px box for a 34px mark, and the 10px is not padding for looks.
        PixelPet scales its body 1.25× about the centre and its tallest accessory
        reaches y = -8.25 in a 0..64 viewBox, with overflow: visible — it paints
        OUTSIDE its own box by design. Room needed is (frame - S)/2 >= 8.25 · S/64,
        i.e. frame >= 1.258·S; 44 clears 34.

        Nothing in this row clips, so the box is not saving the antenna from being
        cut — it is keeping it out of the row above. The distinction matters because
        this app once recorded the wrong one and it outlived the fix it explained
        (see the chip comment in App.tsx).
      */}
      <span className="act-av">
        {/*
          An agent gets its own face; a human or an unattributed row gets the room's
          mark, seeded on the PROJECT ID (what ProjectAvatar's geometry is derived
          from) rather than on its display name. Never a generated pet for someone
          with no channel hash — that is a confident face for an identity that does
          not exist.
        */}
        {item.authorHash ? (
          <PixelPet name={item.author} seed={item.authorHash} size={34} />
        ) : (
          <ProjectAvatar
            /* A DM has no project id to seed from; its peer's address is the stable
               identity the rail seeds that person's tile from too. */
            projectId={
              item.open.kind === 'project'
                ? item.open.id
                : item.open.kind === 'dm'
                  ? item.open.peer
                  : item.where
            }
            label={item.author}
            size={34}
          />
        )}
        <span className="act-badge" aria-hidden="true">
          {item.kind === 'project' ? '#' : '@'}
        </span>
      </span>

      <span className="act-body">
        <span className="act-hd">
          <span className="act-name">{item.author}</span>
          <span className="act-when">{relativeTime(item.ts, now)}</span>
          {item.unread && <span className="act-dot" aria-label="unread" />}
        </span>
        <span className="act-ctx">{item.context}</span>
        <span className="act-text">{item.text}</span>
      </span>
    </button>
  )
}
